import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AccountContext } from "../../contexts/AccountContext";
import LinkPerformance from "./LinkPerformance";
import SummaryLink from "./SummaryLink";
import ExportDropbox from "./ExportDropbox";
import LoadingScreen from "../LoadingScreen";
import { CONFIG } from "../../config";
import Container from "react-bootstrap/esm/Container";
import Col from "react-bootstrap/esm/Col";
import Row from "react-bootstrap/esm/Row";
import Button from "react-bootstrap/esm/Button";
import DualBandBudget from "./DualBandBudget";
import { Chart } from 'chart.js';

// Component to send the account data and get url parameters
export default function Budget() {
    const { id } = useParams()
    const account = useContext(AccountContext); // Send to SimpleLinkComponent the user's token
    return <BudgetComponent accountToken={account.token} linkId={id} />
}


export function BudgetComponent(props) {

    const isLoading = useRef(false);

    const [getChartImg, setChartImg] = useState("");

    const [link, setLink] = useState({});

    const refChart = useRef(null);

    // Get the reference data
    useEffect(function() {
        if(!isLoading.current) {
            fetch(CONFIG.apiURI+"/link/"+props.linkId+"/budget", { 
                method: 'GET',
                mode: "cors",
                headers: {
                    Accept: 'application/json',
                    "token": props.accountToken
                }
                })
            .then((res) => res.json())
            .then((linkData) => {
                setLink(linkData)
                isLoading.current = true; 

                const ctx = document.getElementById('chart-render');
                refChart.current = new Chart(ctx, {
                    type: 'line',
                    data: configuration(linkData),
                    options: {
                        legend: {
                            display: false
                        },
                        scales: {
                            y: {
                                reverse: true,
                                title: {
                                    display: true,
                                    text: 'Availability [%]'
                                },
                                ticks: {
                                    precision: 7,
                                    callback: function(value, index, ticks) {
                                        return value+"%";
                                    }
                                }
                            },
                            x: {
                                title: {
                                    display: true,
                                    text: 'Capacity [Mbps]'
                                },
                            }
                        },
                        animation: {
                            duration: 100,
                            onComplete: function(chart) {
                                setChartImg(refChart.current.toBase64Image()) // Generate an image
                            }
                        },
                        plugins: {
                            title: {
                                display: true,
                                text: 'Dual band capacity / availability',
                                font: {
                                    size: 18
                                }
                            },
                            legend: {
                                labels: {
                                    // Remove all legends
                                  filter: function(item, chart) {
                                    return false;
                                  }
                                }
                            }
                        }
                    }
                });
            })
        }
    } , [])

    
   // }  

    /**
     * Transform data to be placed in the data grid
     * @param linkData 
     * @returns either an array (1D) in single band or an object in dual band
     */
    function serializeDataGrid(linkData) {

        // If true then it's single band
        if(linkData?.secondBand == null) {
            let jsonCalculateData = linkData?.data;
            if(jsonCalculateData?.modulations == undefined) return [];
            
            let res = [];
            for (let i = 0; i < jsonCalculateData.modulations.length; i++) {
                const line = {
                    modulation:Number,
                    capacity: Number,
                    availability: Number,
                    outage: Number,
                    fadeMargin: Number,
                    txPower: Number,
                    rxLevel: Number
                };
                line.modulation = jsonCalculateData.modulations[i];
                line.capacity = jsonCalculateData.capacities[i].toFixed(1);
                line.availability = jsonCalculateData.availabilities[i].toFixed(7);
                line.outage = jsonCalculateData.outages[i]; // heure minute seconde
                line.fadeMargin = jsonCalculateData.fadeMargins[i].toFixed(2);
                line.txPower = jsonCalculateData.txPowers[i].toFixed(2);
                line.rxLevel = jsonCalculateData.rxLevels[i].toFixed(2);
                res.push(line);
            }
            return res;

        }

        /**
         * For second band
         */

        let res = [];


        // take the band that have the most number of modulations
        let totalLine = (linkData?.firstBand.data.modulations.length > linkData?.secondBand.data.modulations.length) ? linkData?.firstBand.data.modulations.length : linkData?.secondBand.data.modulations.length;

        for (let i = 0; i < totalLine; i++) {
            let line = {}

            if(linkData?.firstBand.data.modulations[i] != null) {
                const firstBand = linkData?.firstBand.data;

                line.modulation = firstBand.modulations[i];
                line.capacity = firstBand.capacities[i].toFixed(1);
                line.availability = firstBand.availabilities[i].toFixed(7);
                line.outage = firstBand.outages[i]; // hours minutes seconds
                line.fadeMargin = firstBand.fadeMargins[i].toFixed(2);
                line.txPower = firstBand.txPowers[i].toFixed(2);
                line.rxLevel = firstBand.rxLevels[i].toFixed(2);
            }

            if(linkData?.secondBand?.data.modulations[i] != null) {
                const secondBand = linkData?.secondBand.data;

                line.modulation2 = secondBand.modulations[i];
                line.capacity2 = secondBand.capacities[i].toFixed(1);
                line.availability2 = secondBand.availabilities[i].toFixed(7);
                line.outage2 = secondBand.outages[i]; // heure minute seconde
                line.fadeMargin2 = secondBand.fadeMargins[i].toFixed(2);
                line.txPower2 = secondBand.txPowers[i].toFixed(2);
                line.rxLevel2 = secondBand.rxLevels[i].toFixed(2);
            }

            res.push(line)
        }

        return res
    }

    /**
     * methods to draw the chart
     * @param data 
     * @returns 
     */
    function configuration(data) {
        let chartData = drawingData(data)
        
        return {
            labels: chartData.labels,
            datasets: [{
                label: 'Link',
                data: chartData.values,
                fill: false,
                backgroundColor: '#005AFF',
                stepped: true
            }]
        }
    }

    /**
     * Return the data with coordinate format to display points rightly on the chart
     * 
     * Check documentation 'software architecture' to more details about it
     * @param bandData 
     * @returns {labels: [capacities], values: [{x: , y: }, {x: , y:}] }
     */
    function drawingData(bandData) {
        let labels = []
        let values = []

        for (let i = 0; i < bandData.firstBand.data.capacities.length; i++) {
            if(bandData.firstBand.data.availabilities[i] > 0) {
                let val = {
                    band: 1,
                    x: parseFloat(bandData.firstBand.data.capacities[i].toFixed(1)),
                    y: parseFloat(bandData.firstBand.data.availabilities[i])
                }
                values.push(val)
            }
        }
        
        for (let i = 0; i < bandData.secondBand.data.capacities.length; i++) {
            if(bandData.secondBand.data.availabilities[i] > 0) {
                let val = {
                    band: 2,
                    x: parseFloat(bandData.secondBand.data.capacities[i].toFixed(1)),
                    y: parseFloat(bandData.secondBand.data.availabilities[i])
                }
                values.push(val)
            }
            
        }
        
        values.sort((a,b) => b.y - a.y)
        

        // Store final values to return them
        let returnedValues = []

        let latestB1 = 0
        let latestB2 = 0

        for (let i = 0; i < values.length; i++) {

            if(i == (values.length - 1)) {
                if(values[i].band == 1) {
                    returnedValues.push({
                        x: (values[i].x + latestB2).toFixed(1),
                        y: values[i].y
                    })
                } else {
                    returnedValues.push({
                        x: (values[i].x + latestB1).toFixed(1),
                        y: values[i].y
                    })
                }

            // from band 1 to band 1
            } else if(values[i].band == values[i+1].band && values[i].band == 1) {
                returnedValues.push({
                    x: (values[i].x + latestB2).toFixed(1),
                    y: values[i].y
                })

                returnedValues.push({
                    x: (values[i].x + latestB2).toFixed(1),
                    y: values[i+1].y
                })

                latestB1 = values[i].x

            // from band 2 to band 2
            } else if(values[i].band == values[i+1].band && values[i].band == 2) {
                returnedValues.push({
                    x: (values[i].x + latestB1).toFixed(1),
                    y: values[i].y
                })

                returnedValues.push({
                    x: (values[i].x + latestB1).toFixed(1),
                    y: values[i+1].y
                })

                latestB2 = values[i].x

            // from band 1 to band 2
            } else if(values[i].band != values[i+1].band && values[i].band == 1) {
                returnedValues.push({
                    x: (values[i].x + latestB2).toFixed(1),
                    y: values[i].y
                })

                returnedValues.push({
                    x: (values[i].x + latestB2).toFixed(1),
                    y: values[i+1].y
                })

                latestB1 = values[i].x

            // from band 2 to band 1
            } else if(values[i].band != values[i+1].band && values[i].band == 2) {
                returnedValues.push({
                    x: (values[i].x + latestB1).toFixed(1),
                    y: values[i].y
                })

                returnedValues.push({
                    x: (values[i].x + latestB1).toFixed(1),
                    y: values[i+1].y
                })

                latestB2 = values[i].x
            }

            labels.push(returnedValues[returnedValues.length-1].x)
        }
        
        labels.sort((a,b) => a - b)

        return {"labels": labels, "values": returnedValues};
    }

    return (<>
        <LoadingScreen displayed={!isLoading.current} />
        <Container className="mt-3">

            <canvas className="d-none" id="chart-render"></canvas>

            { link.secondBand != null ?
            <>
            <Row>
                <Col className="col-5">
                    <Button className="menu-button" bsPrefix="" variant="secondary" onClick={() => window.location.href = "/"}>My links</Button>
                    <Button className="menu-button ms-2" bsPrefix="" variant="secondary" onClick={() => window.location.href = "/link/"+link.firstBand?.link.idLink+""}>Link configuration</Button>
                    <Button className="menu-button-active ms-2" bsPrefix="" variant="secondary">Link budget</Button>
                    {/*<Button className="menu-button ms-2" bsPrefix="" variant="secondary" onClick={() => window.location.href = "/link/"+link.firstBand?.link.idLink+"/boq"}>Link BoM</Button>*/}
                </Col>
                <Col className="col-4">
                    <div><h5 className="h5 ps-4 pt-2">{link.link?.name}</h5></div>
                </Col>                
                <Col>
                    <ExportDropbox link={link} chart={getChartImg} />
                </Col>
            </Row>

            <Row>
                <Col>
                    <DualBandBudget data={serializeDataGrid(link)} link={link} />
                </Col>
            </Row>


            <Row className="mt-3">
                <img src={`${getChartImg}`} />
            </Row>
            </>

            :

                <>
                    <Row>
                        <Col className="col-5">
                            <Button className="menu-button" bsPrefix="" variant="secondary" onClick={() => window.location.href = "/"}>My links</Button>
                            <Button className="menu-button ms-2" bsPrefix="" variant="secondary" onClick={() => window.location.href = "/link/"+link.link.idLink+""}>Link configuration</Button>
                            <Button className="menu-button-active ms-2" bsPrefix="" variant="secondary">Link budget</Button>
                            <Button className="menu-button ms-2" bsPrefix="" variant="secondary" onClick={() => window.location.href = "/link/"+link.link.idLink+"/boq"}>Link BoM</Button>
                        </Col>
                        <Col className="col-4">
                            <div><h5 className="h5 ps-4 pt-2">{link.link?.name}</h5></div>
                        </Col>
                        <Col>
                            <ExportDropbox link={link} />
                        </Col>
                    </Row>

                    <Row className="mt-3 d-none d-lg-flex">
                        <Col md={3}>
                            <SummaryLink edited_miles={link?.edited_miles} data={link.data?.form} advancedLink={link.link?.idLinkType == 2} linkName={link.link?.name} equipmentType={link.link?.type}></SummaryLink>
                        </Col>
                        <Col md={9}>
                            <LinkPerformance data={serializeDataGrid(link)}></LinkPerformance>
                        </Col>
                    </Row>

                    <Row className="mt-3 d-flex d-lg-none">
                        <Col>
                            <LinkPerformance data={serializeDataGrid(link)}></LinkPerformance>
                        </Col>
                    </Row>

                    <Row className="mt-3 d-flex d-lg-none">
                        <Col>
                            <SummaryLink edited_miles={link?.edited_miles} data={link.data?.form} advancedLink={link.link?.idLinkType == 2} linkName={link.link?.name} equipmentType={link.link?.type}></SummaryLink>
                        </Col>
                    </Row>
                </>

            }

        </Container>
    </>);
}
